// @ts-strict-ignore
import { GetStaticPaths } from 'next';
import { useIntl } from 'react-intl';

import {
  HomePageDataDocument,
  HomePageDataQuery,
  Playlist,
  Ribbon,
  TopicLandingDataDocument,
  TopicListDocument,
  TopicListItem,
  Video
} from 'api';

import { sortBy } from 'f';
import { capitalizeFirstLetter } from 'lib/f';
import getArticlesFromRSS from 'lib/get-articles-from-rss';
import { initializeApollo } from 'lib/graphql/client';
import { createLogger } from 'lib/logging';
import {
  fetchPlatformData,
  fetchTrendingDataFromS3,
  TrendingData
} from 'lib/pages/homepage/utils';
import { fetchSailthruData, SailthruDataContentType } from 'lib/sailthru';
import { deduplicateSailthruContent } from 'lib/sailthru/dedupeContent';

import { Layout } from 'components/layout';
import TopicHead from 'components/pages/topic/slug/head';
import TopicPage from 'components/pages/topics/TopicPage';

import {
  getTopicConfig,
  shouldUseNewLayout,
  TopicConfigData
} from 'lib/topics/config';
import { TopicPageDocument } from 'prismic-types';
import { createClient } from 'prismicio';
import hardcodedTopicsData from './topics.en.json';

export type Article = {
  title: string;
  link: string;
  thumbnail?: string;
  description: string;
  datePosted: string;
};

type TopicPageProps = {
  articles: Article[];
  articlesTag: string;
  articlesTotalCount: number;
  name: string;
  playlists: Playlist[];
  playlistsTotalCount: number;
  slug: string;
  talks: Video[];
  talksTotalCount: number;
  topicsList: TopicListItem[];
  programmerRibbons: Array<Ribbon>;
  sailthruDataContent: SailthruDataContentType;
  useNewLayout?: boolean;
  topicConfig?: TopicConfigData;
  topicPageData?: TopicPageDocument;
};

const Topic = ({
  articles,
  articlesTag,
  articlesTotalCount,
  name,
  playlists,
  playlistsTotalCount,
  slug,
  talks,
  talksTotalCount,
  topicsList,
  programmerRibbons,
  sailthruDataContent,
  useNewLayout = false,
  topicConfig,
  topicPageData
}: TopicPageProps) => {
  const { formatMessage: t } = useIntl();

  return (
    <Layout>
      <TopicHead
        description={`Explore TED Talks on ${name}`}
        title={t(
          {
            defaultMessage: 'Ideas about {topicName}',
            id: 'topic.head.title'
          },
          { topicName: name }
        )}
      />
      <TopicPage
        useNewLayout={useNewLayout}
        topicConfig={topicConfig}
        slug={slug}
        name={name}
        programmerRibbons={programmerRibbons}
        sailthruDataContent={sailthruDataContent}
        articles={articles}
        articlesTag={articlesTag}
        articlesTotalCount={articlesTotalCount}
        playlists={playlists}
        playlistsTotalCount={playlistsTotalCount}
        talks={talks}
        talksTotalCount={talksTotalCount}
        topicsList={topicsList}
        hardcodedTopicsData={hardcodedTopicsData}
        topicPageData={topicPageData}
      />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      // Popular topics can be added here to be pre-rendered
      { params: { slug: 'business' } },
      { params: { slug: 'education' } },
      { params: { slug: 'psychology' } },
      { params: { slug: 'art' } },
      { params: { slug: 'communication' } },
      { params: { slug: 'happiness' } },
      { params: { slug: 'animals' } },
      { params: { slug: 'leadership' } },
      { params: { slug: 'activism' } },
      { params: { slug: 'mental+health' } },
      { params: { slug: 'climate+change' } }
    ],
    fallback: 'blocking'
  };
};

export const getStaticProps = async context => {
  const log = createLogger('getStaticProps');
  const apolloClient = initializeApollo();
  const prismicClient = createClient({ previewData: context.previewData });

  const slug = context.params.slug as string;
  const useNewLayout = await shouldUseNewLayout(slug, context.previewData);
  const topicConfig = await getTopicConfig(slug, context.previewData);

  try {
    // Fetch topics list
    const { data: topicsData, error: topicsError } = await apolloClient.query({
      query: TopicListDocument
    });

    if (topicsError) {
      log.error('getting topics list failed', topicsError);
    }

    // Sort topic list alphabetically by name
    const topicsList: TopicListItem[] = [...topicsData.topicList.nodes].sort(
      (a: TopicListItem, b: TopicListItem) =>
        (a.name?.toUpperCase() || '') > (b.name?.toUpperCase() || '') ? 1 : -1
    );

    // Fetch topic data
    const { data, error } = await apolloClient.query({
      query: TopicLandingDataDocument,
      variables: { slug }
    });

    // If the requested slug does not exist as a topic, redirect to search
    if (!data.topic?.name) {
      return {
        redirect: {
          destination: `/search?q=${slug}`
        }
      };
    }

    // Articles are coming from WordPress and tags are using dashes instead of pluses
    const articlesTag = encodeURIComponent(slug.replace(/\+/g, '-'));

    const {
      articles,
      articlesTotalCount,
      error: articlesError
    } = await getArticlesFromRSS(articlesTag);

    let programmerRibbons: Array<Ribbon> = [];
    let sailthruDataContent: SailthruDataContentType =
      {} as SailthruDataContentType;
    let topicPageData: TopicPageDocument | undefined = undefined;

    // Fetch additional data for new layout topics
    if (useNewLayout) {
      try {
        // Fetch topic_page document
        try {
          const topicPageDocs = await prismicClient.getAllByType('topic_page');
          const topicPageDoc = topicPageDocs.find((doc: TopicPageDocument) => {
            const data = doc.data;
            return data.topic_slug?.toLowerCase() === slug.toLowerCase();
          });

          if (topicPageDoc) {
            topicPageData = topicPageDoc;
          } else {
            log.warn('Topic page document not found for slug:', slug);
          }
        } catch (topicPageError) {
          log.warn(
            'Error fetching topic page document for slug:',
            slug,
            topicPageError
          );
          // Continue without topic page data - will use defaults
        }

        const [trendingData, sailthruData] = await Promise.all([
          fetchTrendingDataFromS3().catch(err => {
            log.error('Fetching trending data failed', err);
            return { trendingTalks: [] } as TrendingData;
          }),
          fetchSailthruData()
        ]);

        const validTrendingData = trendingData as TrendingData;

        const queryResult = await apolloClient.query({
          query: HomePageDataDocument,
          variables: {
            trendingIds: validTrendingData.trendingTalks
              .map((t: { talkId: string }) => t.talkId)
              .slice(0, 17)
          }
        });

        const homeData = queryResult.data as HomePageDataQuery;
        const { programmerRibbons: ribbons } =
          await fetchPlatformData(homeData);
        programmerRibbons = ribbons;

        const featuredSpotlightDataIds = sortBy(
          video => video?.order ?? 0,
          ribbons[0]?.slots?.nodes ?? []
        )
          .map(node => node?.video)
          .slice(0, 5)
          .map(t => t?.id)
          .filter((id): id is string => id !== undefined);

        sailthruDataContent = deduplicateSailthruContent(
          sailthruData,
          featuredSpotlightDataIds
        );
      } catch (error) {
        log.error('Error fetching data for new layout', error);
        // Fall back to classic layout if data fetching fails
      }
    }

    // Logging errors
    if (error) {
      log.error('getting topic data failed', error);
    }

    if (articlesError) {
      log.error('getting articles rss failed', articlesError);
    }

    const cleanTopicConfig = topicConfig
      ? Object.fromEntries(
          Object.entries(topicConfig).filter(
            ([_, value]) => value !== undefined
          )
        )
      : null;

    return {
      props: {
        articles,
        articlesTag,
        articlesTotalCount,
        name: capitalizeFirstLetter(data.topic?.name),
        playlists: data.topic?.playlists?.nodes || [],
        playlistsTotalCount: data.topic?.playlists?.totalCount,
        slug,
        talks: data.topic?.videos?.nodes || [],
        talksTotalCount: data.topic?.videos?.totalCount,
        topicsList,
        programmerRibbons,
        sailthruDataContent,
        useNewLayout,
        topicConfig: cleanTopicConfig,
        topicPageData: topicPageData || null
      },
      revalidate: 180
    };
  } catch (error) {
    log.error('Error in getStaticProps', error);
    return {
      notFound: true,
      revalidate: 30
    };
  }
};

export default Topic;
